<template>
  <div>
    <!-- Loading Overlay -->
    <v-overlay :value="globalLoading" opacity="0.8" z-index="9999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4">{{ loadingMessage }}</div>
      </div>
    </v-overlay>

    <!-- Error Alert -->
    <v-alert
      v-if="error"
      type="error"
      dismissible
      @input="error = null"
      class="ma-4"
    >
      {{ error }}
    </v-alert>

    <!-- Bottom Navigation -->
    <v-bottom-navigation
      :color="COLORS.PRIMARY"
      horizontal
      :background-color="COLORS.PRIMARY"
      dark
    >
      <v-btn to="/college/personnel_etc" large class="mr-10">
        <span class="bottom-navi">ข้อมูลบุคคลสถานศึกษา</span>
        <v-icon>mdi-account-multiple</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerHuman')"
        to="/college/rate_workforce_g"
        large
        class="mr-7"
      >
        <span class="bottom-navi">ข้อมูลอัตราสถานศึกษา</span>
        <v-icon>mdi-numeric-1-box</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerStudent') && hasRateWorkData"
        to="/college/rate_workforce"
        class="mr-15"
      >
        <span class="bottom-navi">ข้อมูลนักเรียนนักศึกษา</span>
        <v-icon>mdi-numeric-2-box</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerCalStD') && hasRateWorkData"
        to="/college/rate_workforce_pro"
        class="mr-14"
      >
        <span class="bottom-navi">ประมวลผลห้องเรียน</span>
        <v-icon>mdi-numeric-3-box</v-icon>
      </v-btn>

      <v-btn
        v-if="canShowWorkforceCal"
        to="/college/rate_workforce_cal"
        class="mr-15"
      >
        <span class="bottom-navi">ประมวลผลอัตรากำลัง</span>
        <v-icon>mdi-numeric-4-box</v-icon>
      </v-btn>

      <v-btn
        v-if="canShowWorkforceCal"
        to="/college/rate_workforce_report"
        class="mr-7"
      >
        <span class="bottom-navi">รายงานผลอัตรากำลัง</span>
        <v-icon>mdi-numeric-5-box</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <!-- Main Container -->
    <v-container id="upgrade" fluid tag="section" class="">
      <v-row>
        <v-col cols="12">
          <base-material-card color="primary">
            <template v-slot:heading>
              <h2 class="font-weight-light">
                <v-icon large left>mdi-file-send</v-icon>
                โปรแกรมคำนวณอัตรากำลังในสถานศึกษา
                สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา :
                {{ user.college_id_code_type_manpower }}
              </h2>
            </template>

            <!-- Year Selection and Basic Info -->
            <v-card class="mx-auto pa-5 mb-4">
              <v-row class="mb-4" justify="center" no-gutters>
                <v-col md="10">
                  <v-card outlined>
                    <v-card-text>
                      <v-row align="center">
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="selectedYear"
                            :items="academicYears"
                            item-value="state"
                            item-text="label"
                            @change="onYearChange"
                            label="ปีการศึกษา พ.ศ."
                            outlined
                            dense
                            :loading="yearLoading"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon>mdi-calendar</v-icon>
                            </template>
                          </v-select>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-center">
                            <h3>รหัสสถานศึกษา: {{ user.user_name }}</h3>
                            <h3>ชื่อสถานศึกษา: {{ user.college_name }}</h3>
                          </div>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-center">
                            <h3>ประเภทสถานศึกษา: {{ user.name_ctm }}</h3>
                            <h3>
                              ปีการศึกษา: {{ currentRateWork.rate_work_g_year }}
                            </h3>

                            <v-btn
                              v-if="isDataConfirmed"
                              rounded
                              color="info"
                              :href="printReportUrl"
                              target="_blank"
                              class="mt-2"
                            >
                              <v-icon left>mdi-printer</v-icon>
                              พิมพ์รายงานข้อมูล
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Warning for Unconfirmed Data -->
              <v-alert
                v-if="!isDataConfirmed"
                type="warning"
                class="text-center mb-4"
              >
                <h2>
                  ท่านยังไม่ได้ยืนยันบันทึกรายการ ในขั้นตอนที่ 4
                  ทำให้ข้อมูลส่วนนี้จะไม่ปรากฎ
                </h2>
              </v-alert>

              <!-- Main Data Section -->
              <div v-if="isDataConfirmed">
                <!-- Summary Table -->
                <v-card outlined elevation="2" class="mb-6">
                  <v-card-title>
                    <v-icon left>mdi-table</v-icon>
                    สรุปข้อมูลอัตรากำลัง
                  </v-card-title>

                  <v-card-text>
                    <div class="table-responsive">
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th class="text-center font-weight-bold">
                              ประเภทอัตรา
                            </th>
                            <th class="text-center font-weight-bold">มีจริง</th>
                            <th
                              class="text-center font-weight-bold grey lighten-1"
                            >
                              จำนวนห้องเรียน
                            </th>
                            <th class="text-center font-weight-bold">เกณฑ์</th>
                            <th
                              class="text-center font-weight-bold grey lighten-1"
                            >
                              อัตรากำลังรวม
                            </th>
                            <th class="text-center font-weight-bold">
                              (-ขาด/+เกิน)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Director Row -->
                          <tr>
                            <td class="font-weight-medium">ผอ.สถานศึกษา</td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_dr || "-" }}
                            </td>
                            <td class="text-center grey lighten-1" rowspan="2">
                              {{ currentRateWork.rate_room_cal_all || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_criterion_dr || "-" }}
                            </td>
                            <td class="text-center grey lighten-1" rowspan="2">
                              {{
                                currentRateWork.rate_man_power_teach_all || "-"
                              }}
                            </td>
                            <td
                              class="text-center"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_criterion_incomplete_dr
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_dr
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Deputy Director Row -->
                          <tr>
                            <td class="font-weight-medium">รอง ผอ.สถานศึกษา</td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sedr || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_criterion_sedr || "-" }}
                            </td>
                            <td
                              class="text-center"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_criterion_incomplete_sedr
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_sedr
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Spacer -->
                          <tr>
                            <td colspan="6" class="pa-2"></td>
                          </tr>

                          <!-- Teachers Summary -->
                          <tr class="teachers-summary">
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.SUCCESS}`"
                            >
                              ครูผู้สอน (รวม)
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.SUCCESS}`"
                            >
                              {{
                                currentRateWork.rate_teach_official_all || "-"
                              }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.SUCCESS}`"
                            >
                              {{ currentRateWork.rate_teach_civil_all || "-" }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.INFO}`"
                            >
                              {{
                                currentRateWork.rate_criterion_teach_all || "-"
                              }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.INFO}`"
                            >
                              {{
                                currentRateWork.rate_criterion_teach_alls || "-"
                              }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.PRIMARY}`"
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_teach_all
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Headers for teacher types -->
                          <tr class="teacher-type-headers">
                            <td></td>
                            <td class="text-center font-weight-medium">
                              ข้าราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              พนักงานราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              ข้าราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              พนักงานราชการ
                            </td>
                            <td class="text-center"></td>
                          </tr>

                          <!-- Teacher Categories -->

                          <tr
                            v-for="category in teacherCategories"
                            :key="category.key"
                          >
                            <td class="font-weight-medium">
                              {{ category.name }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "ta") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "tb") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "ta_official") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "tb_civil") }}
                            </td>
                            <td
                              class="text-center"
                              :class="
                                getShortageClass(
                                  getTeacherData(category.key, 'incomplete')
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  getTeacherData(category.key, "incomplete")
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Support Staff -->
                          <tr>
                            <td class="font-weight-bold">
                              บุคลากรสนับสนุนการสอน
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sup_ta || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sup_tb || "-" }}
                            </td>
                            <td class="text-center">
                              {{
                                currentRateWork.rate_work_g_sup_ta_official ||
                                  "-"
                              }}
                            </td>
                            <td class="text-center">
                              {{
                                currentRateWork.rate_work_g_sup_tb_civil || "-"
                              }}
                            </td>
                            <td
                              class="text-center"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_work_g_sup_incomplete
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_work_g_sup_incomplete
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Total Summary -->
                          <tr class="total-summary">
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.SUCCESS}`"
                            >
                              <h3>รวมอัตรา</h3>
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.SUCCESS}`"
                              colspan="2"
                            >
                              <h3>
                                {{ currentRateWork.rate_man_power_all || "-" }}
                              </h3>
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.INFO}`"
                            >
                              {{ currentRateWork.rate_criterion_all || "-" }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.INFO}`"
                            ></td>
                            <td
                              class="text-center font-weight-bold"
                              :style="`background-color: ${COLORS.INFO}`"
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_all
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Employee Categories Headers -->
                          <tr class="employee-headers">
                            <td></td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างประจำ
                            </td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างชั่วคราว(ครูจ้างสอน)
                            </td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างชั่วคราว(สายสนับสนุน,เจ้าหน้าที่)
                            </td>
                            <td class="text-center"></td>
                            <td class="text-center"></td>
                          </tr>

                          <!-- Employee Data -->
                          <tr>
                            <td class="font-weight-bold">
                              ลูกจ้างประจำ/ชั่วคราว
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_ta || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_tc || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_tb || "-" }}
                            </td>
                            <td class="text-center"></td>
                            <td class="text-center"></td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Detailed Analysis Section -->
                <v-card outlined elevation="2">
                  <v-card-title
                    class="d-flex justify-space-between align-center"
                  >
                    <div>
                      <v-icon left>mdi-chart-line</v-icon>
                      แสดงอัตรากำลังประกอบการตัดสินใจ ผู้บริหารสถานศึกษา
                    </div>
                    <v-chip color="orange" outlined>
                      <v-icon left small>mdi-test-tube</v-icon>
                      อยู่ระหว่างทดสอบสถิติ
                    </v-chip>
                  </v-card-title>

                  <!-- Desktop View -->
                  <v-card-text v-if="$vuetify.breakpoint.mdAndUp">
                    <v-data-table
                      :headers="detailedTableHeaders"
                      :items="processedDetailedData"
                      :loading="detailLoading"
                      :items-per-page="itemsPerPage"
                      :footer-props="tableFooterProps"
                      :search="searchQuery"
                      class="detailed-analysis-table"
                      dense
                    >
                      <template v-slot:top>
                        <v-row class="ma-2">
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="searchQuery"
                              label="ค้นหาสาขาวิชา"
                              prepend-inner-icon="mdi-magnify"
                              clearable
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6" class="text-right">
                            <v-btn
                              v-if="isDataConfirmed"
                              rounded
                              color="info"
                              :href="detailedPrintReportUrl"
                              target="_blank"
                            >
                              <v-icon left>mdi-printer</v-icon>
                              พิมพ์รายงานรายละเอียด
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>

                      <template v-slot:item.manPower="{item}">
                        {{ parseFloat(item.manPower).toFixed(2) }}
                      </template>

                      <!-- Custom slots for better formatting -->
                      <template v-slot:item.percentage="{ item }">
                        <span class="font-weight-medium">
                          {{ item.percentage }}%
                        </span>
                      </template>

                      <template v-slot:item.manPowerCalculated="{ item }">
                        <v-chip
                          small
                          :color="
                            item.manPowerCalculated > 0 ? 'success' : 'grey'
                          "
                          dark
                        >
                          {{ item.manPowerCalculated }}
                        </v-chip>
                      </template>

                      <template v-slot:item.officialShortage="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.officialShortage)"
                          dark
                        >
                          {{ formatShortage(item.officialShortage) }}
                        </v-chip>
                      </template>

                      <template v-slot:item.civilShortage="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.civilShortage)"
                          dark
                        >
                          {{ formatShortage(item.civilShortage) }}
                        </v-chip>
                      </template>

                      <template v-slot:item.overallSummary="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.overallSummary)"
                          dark
                        >
                          {{ formatShortage(item.overallSummary) }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-card-text>

                  <!-- Mobile View -->
                  <v-card-text v-else>
                    <v-text-field
                      v-model="searchQuery"
                      label="ค้นหาสาขาวิชา"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      outlined
                      dense
                      class="mb-4"
                    ></v-text-field>

                    <div v-if="detailLoading" class="text-center pa-4">
                      <v-progress-circular indeterminate></v-progress-circular>
                      <div class="mt-2">กำลังโหลดข้อมูล...</div>
                    </div>

                    <v-expansion-panels
                      v-else-if="processedDetailedData.length > 0"
                      accordion
                    >
                      <v-expansion-panel
                        v-for="item in filteredMobileData"
                        :key="item.index"
                      >
                        <v-expansion-panel-header>
                          <div>
                            <div class="font-weight-bold">
                              {{ item.branch_name_th }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ item.type_course }} - {{ item.year_course }}
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-simple-table dense>
                            <tbody>
                              <tr>
                                <td class="font-weight-medium">
                                  จำนวนนักเรียน:
                                </td>
                                <td>{{ item.sumStudent }}</td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">ร้อยละ:</td>
                                <td>{{ item.percentage }}%</td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">
                                  อัตรากำลัง/สาขา:
                                </td>
                                <td>
                                  <v-chip small color="success" dark>
                                    {{ item.manPowerCalculated }}
                                  </v-chip>
                                </td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">สรุปภาพรวม:</td>
                                <td>
                                  <v-chip
                                    small
                                    :color="
                                      getShortageChipColor(item.overallSummary)
                                    "
                                    dark
                                  >
                                    {{ formatShortage(item.overallSummary) }}
                                  </v-chip>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Mobile Pagination -->
                    <div
                      class="text-center mt-4"
                      v-if="processedDetailedData.length > mobileItemsPerPage"
                    >
                      <v-pagination
                        v-model="mobilePage"
                        :length="
                          Math.ceil(
                            processedDetailedData.length / mobileItemsPerPage
                          )
                        "
                        :total-visible="5"
                      ></v-pagination>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-card>
          </base-material-card>
        </v-col>
      </v-row>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmationDialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="warning">mdi-alert</v-icon>
            ยืนยันการบันทึกข้อมูล
          </v-card-title>

          <v-card-text>
            <v-alert type="warning" outlined>
              <div class="font-weight-bold">
                การยืนยันบันทึกข้อมูล เมื่อกดยืนยันแล้วจะไม่สามารถแก้ไขข้อมูลใดๆ
                ได้แล้ว กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนการยืนยัน
              </div>
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              large
              @click="confirmationDialog = false"
              :disabled="confirmLoading"
            >
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              large
              color="success"
              @click="confirmDataSubmission"
              :loading="confirmLoading"
            >
              <v-icon left>mdi-content-save</v-icon>
              ยืนยันการบันทึกข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout"
        :color="snackbar.color"
        top
        right
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}

        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
// Constants
const API_ENDPOINTS = {
  USER: "user.php",
  RATE_WORK: "rate_work_g.php",
  RATE_WORK_CAL: "rate_work_college_cal_idbranch.php",
  RATE_WORK_ROOM_CHECK: "rate_work_college_cal_room.php",
  SYS_CONFIG: "sys_config_collegemanpower.php"
};

const COLORS = {
  PRIMARY: "teal darken-4",
  SUCCESS: "green lighten-1",
  INFO: "cyan lighten-2",
  WARNING: "orange lighten-1",
  ERROR: "red lighten-1"
};

const TEACHER_CATEGORIES = [
  { key: "gs", name: "-ประเภทวิชาสามัญ" },
  { key: "is", name: "-ประเภทวิชาอุตสาหกรรม" },
  { key: "cm", name: "-ประเภทวิชาพาณิชยกรรม" },
  { key: "art", name: "-ประเภทวิชาศิลปกรรม" },
  { key: "em", name: "-ประเภทวิชาคหกรรม" },
  { key: "ag", name: "-ประเภทวิชาเกษตรกรรม" },
  { key: "fish", name: "-ประเภทวิชาประมง" },
  { key: "tour", name: "-ประเภทวิชาอุตสาหกรรมการท่องเที่ยว" },
  { key: "textile", name: "-ประเภทวิชาอุตสาหกรรมสิ่งทอ" },
  { key: "insy", name: "-ประเภทวิชาเทคโนโลยีสารสนเทศ" },
  { key: "entertain", name: "-ประเภทวิชาอุตสาหกรรมบันเทิงและดนตรี" },
  { key: "navy", name: "-ประเภทวิชาพาณิชยนาวี" },
  { key: "agriInno", name: "-ประเภทวิชาเทคโนโลยีและนวัตกรรมเกษตรอุตสาหกรรม" },
  { key: "sc", name: "-หลักสูตรระยะสั้น*" }
];

export default {
  name: "ImprovedWorkforceCalculation",

  data() {
    return {
      // Constants
      API_ENDPOINTS,
      COLORS,

      // Loading states
      globalLoading: false,
      yearLoading: false,
      detailLoading: false,
      confirmLoading: false,
      loadingMessage: "กำลังโหลดข้อมูล...",

      // Error handling
      error: null,

      // User and authentication
      user: {},

      // Year selection
      selectedYear: {},
      academicYears: [
        { state: "2565", abbr: "2022", label: "2565 (2022)" },
        { state: "2566", abbr: "2023", label: "2566 (2023)" },
        { state: "2567", abbr: "2024", label: "2567 (2024)" },
        { state: "2568", abbr: "2025", label: "2568 (2025)" },
        { state: "2569", abbr: "2026", label: "2569 (2026)" },
        { state: "2570", abbr: "2027", label: "2570 (2027)" }
      ],

      // Data
      currentRateWork: {},
      detailedData: [],
      sysConfig: {},
      rateWorkCheck: {},
      rateWorkRoomCheck: {},

      // Table configuration
      searchQuery: "",
      itemsPerPage: 25,
      tableFooterProps: {
        "items-per-page-options": [10, 25, 50, 100],
        "items-per-page-text": "แถวต่อหน้า:"
      },

      // Mobile pagination
      mobilePage: 1,
      mobileItemsPerPage: 10,

      // Dialogs
      confirmationDialog: false,

      // Notifications
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Teacher categories
      teacherCategories: TEACHER_CATEGORIES
    };
  },

  computed: {
    // Permission checks
    hasPermission() {
      return permission => {
        return (
          this.sysConfig[`sys_config_${permission}`] === "1"
        );
      };
    },

    hasRateWorkData() {
      return !!this.rateWorkCheck.rate_work_g_college_code;
    },

    canShowWorkforceCal() {
      return this.rateWorkRoomCheck.status !== false && this.hasRateWorkData;
    },

    canShowReport() {
      return this.rateWorkRoomCheck.status !== false;
    },

    isDataConfirmed() {
      return this.currentRateWork.rate_work_g_status === "Confirm";
    },

    // URLs for printing
    printReportUrl() {
      if (!this.isDataConfirmed) return "";
      return `#/college/print_rate_workforce_cal/${this.currentRateWork.rate_work_g_year}/${this.currentRateWork.college_code}`;
    },

    detailedPrintReportUrl() {
      if (!this.isDataConfirmed) return "";
      return `#/college/print_rate_workforce_cal2/${this.currentRateWork.rate_work_g_year}/${this.currentRateWork.college_code}`;
    },

    // Table headers for detailed analysis
    detailedTableHeaders() {
      return [
        {
          text: "ลำดับ",
          value: "index",
          align: "center",
          sortable: false,
          width: "60px"
        },
        {
          text: "ประเภท",
          value: "type_course",
          align: "start",
          width: "100px"
        },
        {
          text: "หลักสูตร",
          value: "year_course",
          align: "center",
          width: "80px"
        },
        { text: "สาขาวิชา", value: "branch_name_th", align: "start" },
        {
          text: "จำนวนนักเรียน",
          value: "sumStudent",
          align: "center",
          width: "120px"
        },
        {
          text: "รวม/จำนวนนักเรียนประเภท",
          value: "sumAllStd",
          align: "center",
          width: "150px"
        },
        { text: "ร้อยละ", value: "percentage", align: "center", width: "80px" },
        {
          text: "อัตรากำลังคำนวณ",
          value: "manPower",
          align: "center",
          width: "120px"
        },
        {
          text: "อัตรากำลัง/สาขา",
          value: "manPowerCalculated",
          align: "center",
          width: "120px"
        },
        {
          text: "ข้าราชการ",
          value: "officialRate",
          align: "center",
          width: "100px"
        },
        {
          text: "ข้าราชการ(มีอยู่)",
          value: "countTeach",
          align: "center",
          width: "120px"
        },
        {
          text: "ข้าราชการ(ขาด)",
          value: "officialShortage",
          align: "center",
          width: "100px"
        },
        { text: "พนง.", value: "civilRate", align: "center", width: "80px" },
        {
          text: "พนง.(มีอยู่)",
          value: "countTeachGov",
          align: "center",
          width: "100px"
        },
        {
          text: "พนง.(ขาด)",
          value: "civilShortage",
          align: "center",
          width: "100px"
        },
        {
          text: "ครูจ้างสอน",
          value: "countTeachTem",
          align: "center",
          width: "100px"
        },
        {
          text: "สรุปภาพรวม",
          value: "overallSummary",
          align: "center",
          width: "120px"
        }
      ];
    },

    // Processed data for detailed table
    processedDetailedData() {
      // ตรวจสอบว่า detailedData เป็น array และมีข้อมูล
      if (!Array.isArray(this.detailedData) || this.detailedData.length === 0) {
        return [];
      }

      return this.detailedData.map((item, index) => {
        const percentage = this.calculatePercentage(
          item.sumStudent,
          item.sumAllStd
        );
        const manPowerCalculated = this.calculateManPowerPerBranch(
          item,
          percentage
        );
        const officialRate = this.calculateOfficialRate(manPowerCalculated);
        const civilRate = this.calculateCivilRate(manPowerCalculated);
        const officialShortage = this.calculateOfficialShortage(
          item,
          officialRate
        );
        const civilShortage = this.calculateCivilShortage(item, civilRate);
        const overallSummary = this.calculateOverallSummary(
          item,
          manPowerCalculated
        );

        return {
          ...item,
          index: index + 1,
          percentage,
          manPowerCalculated,
          officialRate,
          civilRate,
          officialShortage,
          civilShortage,
          overallSummary
        };
      });
    },

    // Filtered data for mobile view
    filteredMobileData() {
      let filtered = this.processedDetailedData;

      // ตรวจสอบว่ามีข้อมูลก่อน filter
      if (!Array.isArray(filtered) || filtered.length === 0) {
        return [];
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          item =>
            (item.branch_name_th &&
              item.branch_name_th.toLowerCase().includes(query)) ||
            (item.type_course && item.type_course.toLowerCase().includes(query))
        );
      }

      // Apply pagination for mobile
      const start = (this.mobilePage - 1) * this.mobileItemsPerPage;
      const end = start + this.mobileItemsPerPage;
      return filtered.slice(start, end);
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    // Initialization
    async initializeComponent() {
      this.globalLoading = true;
      this.loadingMessage = "กำลังโหลดข้อมูลเริ่มต้น...";

      try {
        // Load user data first
        await this.loadUserData();

        // Load all other data in parallel
        await Promise.allSettled([
          //this.loadRateWorkData(),
          //this.loadDetailedData(),
          this.loadSystemConfig(),
          //.loadRateWorkChecks()
        ]);
      } catch (error) {
        this.handleError("ไม่สามารถโหลดข้อมูลได้", error);
      } finally {
        this.globalLoading = false;
      }
    },

    // Data loading methods
    async loadUserData() {
      try {
        const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        const result = await this.$http.post(API_ENDPOINTS.USER, {
          ApiKey: "HRvec2021",
          user_ID: userSession.user_ID
        });
        this.user = result.data;
      } catch (error) {
        throw new Error("ไม่สามารถโหลดข้อมูลผู้ใช้ได้");
      }
    },

    async loadRateWorkData() {
      try {
        this.currentRateWork= {};
      let result = await this.$http.post(API_ENDPOINTS.RATE_WORK, {
          ApiKey: "HRvec2021",
          rate_work_g_college_code: this.user.user_code,
          rate_work_g_year: this.selectedYear
        });
        this.currentRateWork = result.data;
      } catch (error) {
        console.error("Error loading rate work data:", error);
      }
    },

    async loadDetailedData() {
      this.detailLoading = true;
      try {
        const result = await this.$http.post(API_ENDPOINTS.RATE_WORK_CAL, {
          ApiKey: "HRvec2021",
          rate_work_college_code: this.user.user_code,
          rate_work_college_year: this.selectedYear
        });

        // ตรวจสอบและแปลง response ให้เป็น array
        if (result && result.data) {
          this.detailedData = Array.isArray(result.data) ? result.data : [];
        } else {
          this.detailedData = [];
        }
      } catch (error) {
        console.error("Error loading detailed data:", error);
        this.detailedData = []; // ตั้งค่าเป็น array เปล่าเมื่อเกิด error
        this.handleError("ไม่สามารถโหลดข้อมูลรายละเอียดได้", error);
      } finally {
        this.detailLoading = false;
      }
    },

    async loadSystemConfig() {
      try {
        const result = await this.$http.post(API_ENDPOINTS.SYS_CONFIG, {
          ApiKey: "HRvec2021",
          sys_config_CollegemanpowerID: "1"
        });
        this.sysConfig = result.data;
      } catch (error) {
        console.error("Error loading system config:", error);
      }
    },

    async loadRateWorkChecks() {
      try {
        const [rateWorkResult, roomCheckResult] = await Promise.allSettled([
          this.$http.post(API_ENDPOINTS.RATE_WORK, {
            ApiKey: "HRvec2021",
            rate_work_g_college_code: this.user.user_code,
            rate_work_g_year: this.selectedYear
          }),
          this.$http.post(API_ENDPOINTS.RATE_WORK_ROOM_CHECK, {
            ApiKey: "HRvec2021",
            rate_work_ccr_code: this.user.user_code,
            rate_work_ccr_year: this.selectedYear,
            rate_work_ccr_check: "OK"
          })
        ]);

        if (rateWorkResult.status) {
          this.rateWorkCheck = rateWorkResult.value.data;
        }

        if (roomCheckResult.status) {
          this.rateWorkRoomCheck = roomCheckResult.value.data;
        }   
      } catch (error) {
        console.error("Error loading rate work checks:", error);
      }
    },

    // Event handlers
    async onYearChange() {
      this.yearLoading = true;
      this.loadingMessage = "กำลังโหลดข้อมูลปีการศึกษา...";

      try {
        await Promise.allSettled([
          this.loadRateWorkData(),
          this.loadDetailedData(),
          this.loadRateWorkChecks()
        ]);
        this.showSuccessMessage("โหลดข้อมูลปีการศึกษาเรียบร้อยแล้ว");
      } catch (error) {
        this.handleError("ไม่สามารถโหลดข้อมูลปีการศึกษาได้", error);
      } finally {
        this.yearLoading = false;
      }
    },

    async confirmDataSubmission() {
      this.confirmLoading = true;
      try {
        // Implement confirmation logic here
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
        this.showSuccessMessage("ยืนยันข้อมูลเรียบร้อยแล้ว");
        this.confirmationDialog = false;
        await this.loadRateWorkData(); // Reload data
      } catch (error) {
        this.handleError("ไม่สามารถยืนยันข้อมูลได้", error);
      } finally {
        this.confirmLoading = false;
      }
    },

    // Calculation methods
    calculatePercentage(sumStudent, sumAllStd) {
      if (!sumAllStd || sumAllStd === 0 || !sumStudent) return "0.00";
      return parseFloat(
        (parseFloat(sumStudent) * 100) / parseFloat(sumAllStd)
      ).toFixed(2);
    },

    calculateManPowerPerBranch(item, percentage = null) {
      if (!item || !item.manPower) return "0.00";
      if (percentage === null) {
        percentage = this.calculatePercentage(item.sumStudent, item.sumAllStd);
      }
      return parseFloat(
        (parseFloat(percentage) * parseFloat(item.manPower)) / 100
      ).toFixed(2);
    },

    calculateOfficialRate(manPowerCalculated) {
      if (!manPowerCalculated) return "0.00";
      return parseFloat(parseFloat(manPowerCalculated) * 0.9).toFixed(2);
    },

    calculateCivilRate(manPowerCalculated) {
      if (!manPowerCalculated) return "0.00";
      return parseFloat(parseFloat(manPowerCalculated) * 0.1).toFixed(2);
    },

    calculateOfficialShortage(item, officialRate) {
      if (!item || !officialRate) return 0;
      return Math.round(
        parseFloat(item.countTeach || 0) - parseFloat(officialRate)
      );
    },

    calculateCivilShortage(item, civilRate) {
      if (!item || !civilRate) return 0;
      return Math.round(
        parseFloat(item.countTeachGov || 0) - parseFloat(civilRate)
      );
    },

    calculateOverallSummary(item, manPowerCalculated) {
      if (!item || !manPowerCalculated) return 0;
      const totalCurrent =
        parseFloat(item.countTeachTem || 0) +
        parseFloat(item.countTeach || 0) +
        parseFloat(item.countTeachGov || 0);
      return Math.round(totalCurrent - parseFloat(manPowerCalculated));
    },

    // Helper methods
    getTeacherData(categoryKey, field) {
      const key = `rate_work_g_${categoryKey}_${field}`;
      return this.currentRateWork[key] || "-";
    },

    formatShortage(value) {
      if (value === null || value === undefined || value === "-") return "-";
      const num = parseFloat(value);
      if (num > 0) return `+${num}`;
      return num.toString();
    },

    getShortageClass(value) {
      if (value === null || value === undefined || value === "-") return "";
      const num = parseFloat(value);
      if (num > 0) return "green--text";
      if (num < 0) return "red--text";
      return "";
    },

    getShortageChipColor(value) {
      if (value === null || value === undefined || value === "-") return "grey";
      const num = parseFloat(value);
      if (num > 0) return "success";
      if (num < 0) return "error";
      return "warning";
    },

    // Notification methods
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        color: "success",
        text: message,
        icon: "mdi-check-circle",
        timeout: 5000
      };
    },

    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        color: "error",
        text: message,
        icon: "mdi-alert-circle",
        timeout: 8000
      };
    },

    showWarningMessage(message) {
      this.snackbar = {
        show: true,
        color: "warning",
        text: message,
        icon: "mdi-alert",
        timeout: 6000
      };
    },

    // Error handling
    handleError(message, error = null) {
      console.error("Application Error:", error);
      this.error = message;
      this.showErrorMessage(message);
    }
  },

  // Cleanup
  beforeDestroy() {
    // Clear any intervals or timeouts if needed
  }
};
</script>

<style scoped>
/* Global styles */
.text_google {
  font-family: "Sarabun", sans-serif;
}

.bottom-navi {
  font-size: 12pt;
}

/* Table styles */
.table-responsive {
  overflow-x: auto;
}

.detailed-analysis-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

/* Custom table row colors */
.teachers-summary {
  background-color: rgba(76, 175, 80, 0.1);
}

.teacher-type-headers {
  background-color: rgba(0, 0, 0, 0.05);
}

.total-summary {
  background-color: rgba(76, 175, 80, 0.1);
}

.employee-headers {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Card enhancements */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bottom-navi {
    font-size: 10pt;
  }

  .v-btn {
    min-width: auto !important;
    padding: 0 8px !important;
  }

  .v-btn .v-icon {
    margin-left: 4px !important;
    margin-right: 0 !important;
  }
}

/* Loading spinner customization */
.v-overlay .v-progress-circular {
  margin: 1rem;
}

/* Chip customizations */
.v-chip {
  font-weight: 500;
}

/* Expansion panel customizations */
.v-expansion-panel-header {
  padding: 12px 16px;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 8px 16px 16px;
}

/* Table cell padding adjustments */
.v-simple-table td {
  padding: 8px 12px !important;
}

.v-simple-table th {
  padding: 8px 12px !important;
  font-weight: 600 !important;
}

/* Data table improvements */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-data-table >>> thead th {
  background-color: rgba(0, 0, 0, 0.05);
  font-weight: 600;
}

/* Snackbar positioning */
.v-snackbar {
  margin-top: 64px; /* Account for app bar */
}

/* Dialog improvements */
.v-dialog .v-card {
  border-radius: 12px;
}

/* Alert improvements */
.v-alert {
  border-radius: 8px;
  margin: 8px 0;
}

/* Button improvements */
.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-btn.v-btn--rounded {
  border-radius: 24px;
}

/* Text field improvements */
.v-text-field.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px;
}

/* Select field improvements */
.v-select.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px;
}

/* Card title improvements */
.v-card__title {
  word-break: break-word;
  line-height: 1.4;
}

/* Chip spacing */
.v-chip + .v-chip {
  margin-left: 4px;
}

/* Progress circular in tables */
.v-data-table .v-progress-circular {
  margin: 20px auto;
}

/* Mobile specific improvements */
@media (max-width: 600px) {
  .v-container {
    padding: 8px;
  }

  .v-card {
    margin: 4px 0;
  }

  .v-card__title {
    font-size: 1.1rem;
    padding: 12px;
  }

  .v-card__text {
    padding: 8px 12px;
  }
}

/* Print styles */
@media print {
  .v-bottom-navigation,
  .v-btn,
  .v-snackbar,
  .v-overlay {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }
}

/* Accessibility improvements */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.v-chip:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Animation improvements */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Error state styling */
.error-state {
  opacity: 0.7;
  pointer-events: none;
}

/* Success state styling */
.success-state {
  border-left: 4px solid #4caf50;
}

/* Warning state styling */
.warning-state {
  border-left: 4px solid #ff9800;
}
</style>
